import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SimpleGrid,
  Td,
  Textarea,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { FaClipboardList } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  createConsultantActivityReport,
  listConsultantActivityReports,
  submitConsultantActivityReport,
  updateConsultantActivityReport,
} from "../../../api/consultantPortalApi";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
} from "../../../components/Portal/ui";
import { EditIcon } from "@chakra-ui/icons";

// Status -> Chakra colour scheme for StatusBadge dots (registered scales only).
const STATUS_SCHEME = {
  draft: "gray",
  submitted: "blue",
  approved: "green",
  rejected: "red",
};

const createInitialForm = () => {
  const now = new Date();
  return {
    periodYear: now.getFullYear(),
    periodMonth: now.getMonth() + 1,
    billableDays: 0,
    nonBillableDays: 0,
    notes: "",
  };
};

const ActivityReports = () => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(createInitialForm);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listConsultantActivityReports();
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      toast({
        title: t("consultantPortal.activityReports.toasts.loadFailedTitle"),
        description: error?.response?.data?.detail || t("consultantPortal.activityReports.toasts.loadFailedDescription"),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setForm(createInitialForm());
  }, []);

  const handleEdit = useCallback((item) => {
    setEditingId(item.id);
    setForm({
      periodYear: item.periodYear,
      periodMonth: item.periodMonth,
      billableDays: item.billableDays,
      nonBillableDays: item.nonBillableDays,
      notes: item.notes || "",
    });
  }, []);

  const handleSave = useCallback(
    async ({ submitAfterSave = false }) => {
      setSaving(true);
      try {
        const payload = {
          periodYear: Number(form.periodYear),
          periodMonth: Number(form.periodMonth),
          billableDays: Number(form.billableDays),
          nonBillableDays: Number(form.nonBillableDays),
          notes: form.notes || null,
        };
        let saved;
        if (editingId) {
          saved = await updateConsultantActivityReport(editingId, {
            billableDays: payload.billableDays,
            nonBillableDays: payload.nonBillableDays,
            notes: payload.notes,
          });
        } else {
          saved = await createConsultantActivityReport(payload);
        }

        if (submitAfterSave) {
          await submitConsultantActivityReport(saved.id);
        }

        toast({
          title: submitAfterSave ? t("consultantPortal.activityReports.toasts.submitted") : t("consultantPortal.activityReports.toasts.saved"),
          status: "success",
        });
        resetForm();
        fetchReports();
      } catch (error) {
        toast({
          title: t("consultantPortal.activityReports.toasts.saveFailedTitle"),
          description: error?.response?.data?.detail || t("consultantPortal.activityReports.toasts.saveFailedDescription"),
          status: "error",
        });
      } finally {
        setSaving(false);
      }
    },
    [editingId, fetchReports, form, resetForm, toast, t]
  );

  const sortedItems = useMemo(() => {
    return [...items].sort((left, right) => {
      if (left.periodYear !== right.periodYear) return right.periodYear - left.periodYear;
      return right.periodMonth - left.periodMonth;
    });
  }, [items]);

  const columns = [
    { key: "period", label: t("consultantPortal.activityReports.table.period"), width: "20%" },
    { key: "days", label: t("consultantPortal.activityReports.table.days"), width: "16%" },
    { key: "status", label: t("consultantPortal.activityReports.table.status"), width: "20%" },
    { key: "review", label: t("consultantPortal.activityReports.table.review") },
    { key: "actions", label: "", width: "12%", align: "right" },
  ];

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CP"
        eyebrow={t("consultantPortal.common.eyebrow", "Consultant Portal")}
        title={t("consultantPortal.activityReports.pageTitle")}
        actions={
          editingId ? (
            <Button variant="secondary" size="sm" onClick={resetForm}>
              {t("consultantPortal.activityReports.createNewReport")}
            </Button>
          ) : null
        }
      />

      <Box p="20px">
        <Flex gap="12px" align="stretch" direction={{ base: "column", xl: "row" }}>
          <Box flex="1" minW={0} display="flex">
            <Panel
              w="100%"
              title={
                editingId
                  ? t("consultantPortal.activityReports.formTitleEdit")
                  : t("consultantPortal.activityReports.formTitleNew")
              }
            >
              <Box p="14px">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="800" color="portal.muted" textTransform="uppercase" letterSpacing="0.04em" mb="4px">
                      {t("consultantPortal.activityReports.fields.year")}
                    </FormLabel>
                    <Input
                      type="number"
                      value={form.periodYear}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, periodYear: event.target.value }))
                      }
                      isDisabled={Boolean(editingId)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="800" color="portal.muted" textTransform="uppercase" letterSpacing="0.04em" mb="4px">
                      {t("consultantPortal.activityReports.fields.month")}
                    </FormLabel>
                    <Input
                      type="number"
                      min={1}
                      max={12}
                      value={form.periodMonth}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, periodMonth: event.target.value }))
                      }
                      isDisabled={Boolean(editingId)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="800" color="portal.muted" textTransform="uppercase" letterSpacing="0.04em" mb="4px">
                      {t("consultantPortal.activityReports.fields.billableDays")}
                    </FormLabel>
                    <Input
                      type="number"
                      min={0}
                      max={31}
                      value={form.billableDays}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, billableDays: event.target.value }))
                      }
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="800" color="portal.muted" textTransform="uppercase" letterSpacing="0.04em" mb="4px">
                      {t("consultantPortal.activityReports.fields.nonBillableDays")}
                    </FormLabel>
                    <Input
                      type="number"
                      min={0}
                      max={31}
                      value={form.nonBillableDays}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, nonBillableDays: event.target.value }))
                      }
                    />
                  </FormControl>
                </SimpleGrid>
                <FormControl mt="12px">
                  <FormLabel fontSize="11px" fontWeight="800" color="portal.muted" textTransform="uppercase" letterSpacing="0.04em" mb="4px">
                    {t("consultantPortal.activityReports.fields.notes")}
                  </FormLabel>
                  <Textarea
                    value={form.notes}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, notes: event.target.value }))
                    }
                    placeholder={t("consultantPortal.activityReports.placeholders.notes")}
                    rows={5}
                    fontWeight="600"
                  />
                </FormControl>
                <HStack mt="16px" spacing="8px" flexWrap="wrap">
                  <Button variant="secondary" size="sm" isLoading={saving} onClick={() => handleSave({ submitAfterSave: false })}>
                    {t("consultantPortal.activityReports.buttons.saveDraft")}
                  </Button>
                  <Button variant="primary" size="sm" isLoading={saving} onClick={() => handleSave({ submitAfterSave: true })}>
                    {t("consultantPortal.activityReports.buttons.submitReport")}
                  </Button>
                </HStack>
              </Box>
            </Panel>
          </Box>

          <Box flex="1.2" minW={0} display="flex">
            <Panel
              w="100%"
              title={t("consultantPortal.activityReports.historyTitle")}
              count={loading ? t("consultantPortal.common.loading", "Loading...") : sortedItems.length}
            >
              <DataTable
                columns={columns}
                rows={sortedItems}
                loading={loading}
                emptyState={
                  <EmptyState
                    icon={<FaClipboardList />}
                    title={t("consultantPortal.activityReports.empty")}
                    hint={t("consultantPortal.activityReports.emptyHint", "Fill out the form to record your first monthly report.")}
                  />
                }
                renderRow={(item) => (
                  <Tr key={item.id}>
                    <Td fontWeight="700">
                      {`${item.periodYear}-${String(item.periodMonth).padStart(2, "0")}`}
                    </Td>
                    <Td>{`${item.billableDays} / ${item.nonBillableDays}`}</Td>
                    <Td>
                      <StatusBadge
                        label={t(`consultantPortal.activityReports.statusLabels.${item.status}`, item.status)}
                        colorScheme={STATUS_SCHEME[item.status] || "gray"}
                      />
                    </Td>
                    <Td color="portal.muted" title={item.reviewComment || ""}>
                      {item.reviewComment || "-"}
                    </Td>
                    <Td>
                      <Box display="flex" justifyContent="flex-end">
                        {item.status === "draft" || item.status === "rejected" ? (
                          <MiniIconButton
                            aria-label={t("consultantPortal.activityReports.table.edit")}
                            icon={<EditIcon />}
                            onClick={() => handleEdit(item)}
                          />
                        ) : null}
                      </Box>
                    </Td>
                  </Tr>
                )}
              />
            </Panel>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default ActivityReports;
